import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ListingManagement from "./components/ListingManagement";
import ProfileSettings from "./components/ProfileSettings";

interface AuthUser {
  name?: string;
  email?: string;
  userType?: string;
}

function getAuthUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem("auth_user");
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

type Tab = "ilanlarim" | "profil";

const tabs = [
  { id: "ilanlarim" as Tab, label: "İlanlarım", icon: "ri-file-list-line" },
  { id: "profil" as Tab, label: "Profilim", icon: "ri-user-line" },
];

export default function PanelimPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(getAuthUser());
  const [activeTab, setActiveTab] = useState<Tab>("ilanlarim");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/giris", { replace: true });
    }
  }, [user, navigate]);

  const logout = () => {
    localStorage.removeItem("auth_user");
    setUser(null);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" />
          </div>
          <p className="text-sm text-surface-500">Yönlendiriliyor...</p>
        </div>
      </div>
    );
  }

  const activeTabInfo = tabs.find((t) => t.id === activeTab);

  return (
    <div className="min-h-screen bg-surface-50">
      <Navbar />

      <main className="pt-20 md:pt-24 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6 md:mb-8">
              <h1 className="text-xl md:text-2xl font-bold text-surface-900 mb-1">
                Panelim
              </h1>
              <p className="text-sm text-surface-500">
                İlanlarınızı yönetin ve hesap ayarlarınızı düzenleyin
              </p>
            </div>

            {/* Mobile tab dropdown */}
            <div className="md:hidden mb-4">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl border border-surface-200 text-sm font-medium text-surface-700"
              >
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={activeTabInfo?.icon} />
                  </div>
                  {activeTabInfo?.label}
                </span>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i
                    className={
                      mobileMenuOpen
                        ? "ri-arrow-up-s-line"
                        : "ri-arrow-down-s-line"
                    }
                  />
                </div>
              </button>
              {mobileMenuOpen && (
                <div className="mt-2 bg-white rounded-xl border border-surface-200 overflow-hidden shadow-soft">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => {
                        setActiveTab(tab.id);
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                        activeTab === tab.id
                          ? "bg-primary-50 text-primary-600"
                          : "text-surface-600 hover:bg-surface-50"
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={tab.icon} />
                      </div>
                      {tab.label}
                    </button>
                  ))}
                  <div className="border-t border-surface-100">
                    <Link
                      to="/ilan-ver"
                      className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-add-circle-line" />
                      </div>
                      Yeni İlan Ver
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        logout();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors text-left"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-logout-box-r-line" />
                      </div>
                      Çıkış Yap
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-6">
              {/* Desktop sidebar */}
              <aside className="hidden md:block w-64 shrink-0">
                <div className="bg-white rounded-2xl border border-surface-200 shadow-soft overflow-hidden sticky top-24">
                  {/* User card */}
                  <div className="p-5 border-b border-surface-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 text-lg font-bold">
                        {user.name?.charAt(0)?.toUpperCase() || "U"}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-surface-900 truncate">
                          {user.name || user.email}
                        </p>
                        <p className="text-xs text-surface-500 truncate">
                          {user.email}
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium">
                      <i className="ri-shield-check-line" />
                      {user.userType || "Bireysel"}
                    </span>
                  </div>

                  {/* Nav */}
                  <nav className="p-2 space-y-1">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors text-left ${
                          activeTab === tab.id
                            ? "bg-primary-50 text-primary-600"
                            : "text-surface-600 hover:bg-surface-50"
                        }`}
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={tab.icon} />
                        </div>
                        {tab.label}
                      </button>
                    ))}
                    <div className="h-px bg-surface-100 my-2" />
                    <Link
                      to="/ilan-ver"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-add-circle-line" />
                      </div>
                      Yeni İlan Ver
                    </Link>
                    <button
                      type="button"
                      onClick={logout}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-colors text-left"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-logout-box-r-line" />
                      </div>
                      Çıkış Yap
                    </button>
                  </nav>
                </div>
              </aside>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {activeTab === "ilanlarim" && <ListingManagement />}
                {activeTab === "profil" && <ProfileSettings user={user} />}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
